import Anchor from "atoms/anchors/anchor";
import Button from "atoms/buttons/button";
import Heading from "atoms/typography/heading";
import React from "react";
import StringUtils from "utilities/string-utils";
import { ButtonStyles } from "atoms/constants/button-styles";
import { DataTestAttributes } from "interfaces/data-test-attributes";
import { HeadingPriority } from "atoms/constants/heading-priority";

interface TableOfContentsCollapsePanelHeaderProps
    extends Pick<DataTestAttributes, "dataTestId" | "dataTestDisplaySequence"> {
    displayTitle: string;
    isPublic?: boolean;
    to?: string;
    onClick?: () => void;
    description?: string;
    isPdfPublication?: boolean;
}

const TableOfContentsCollapsePanelHeader: React.FunctionComponent<
    TableOfContentsCollapsePanelHeaderProps
> = (props) => {
    const {
        dataTestDisplaySequence,
        dataTestId,
        displayTitle,
        isPublic = false,
        to,
        onClick,
        description,
        isPdfPublication,
    } = props;

    const cssBaseClass = "c-table-of-contents-chapter__heading";
    const cssPublicModifier = "-public";
    const cssClassName = isPublic
        ? StringUtils.joinClassNames([cssBaseClass, cssPublicModifier])
        : cssBaseClass;

    if (isPublic) {
        return (
            <TableOfContentsCollapsePanelHeading
                heading={displayTitle}
                description={description}
                headerCssClass={cssClassName}
                dataTestDisplaySequence={dataTestDisplaySequence}
                dataTestId={dataTestId}
            />
        );
    }

    if (isPdfPublication) {
        return (
            <Button
                cssClassName={cssClassName}
                style={ButtonStyles.Text}
                onClick={onClick}>
                <TableOfContentsCollapsePanelHeading
                    heading={displayTitle}
                    description={description}
                    headerCssClass={`${cssClassName}__header`}
                />
            </Button>
        );
    }

    return (
        <Anchor
            cssClassName={cssClassName}
            dataTestDisplaySequence={dataTestDisplaySequence}
            dataTestId={dataTestId}
            onClick={onClick}
            to={to ?? ""}>
            <TableOfContentsCollapsePanelHeading
                heading={displayTitle}
                headerCssClass={`${cssClassName}__header`}
            />
        </Anchor>
    );
};

export default TableOfContentsCollapsePanelHeader;

// -----------------------------------------------------------------------------------------
// #region Functions
// -----------------------------------------------------------------------------------------

interface TableOfContentsCollapsePanelHeadingProps {
    heading: string;
    description?: string;
    headerCssClass?: string;
    dataTestDisplaySequence?: string | number;
    dataTestId?: string;
}

const TableOfContentsCollapsePanelHeading: React.FunctionComponent<
    TableOfContentsCollapsePanelHeadingProps
> = ({
    heading,
    description,
    headerCssClass,
    dataTestDisplaySequence,
    dataTestId,
}) => {
    return (
        <Heading
            cssClassName={headerCssClass}
            priority={HeadingPriority.Two}
            dataTestDisplaySequence={dataTestDisplaySequence}
            dataTestId={dataTestId}>
            {heading}
            {description && <>&mdash; </>}
            {description}
        </Heading>
    );
};

// #endregion Functions
